import { toast } from 'react-hot-toast';
import { mediaErrorMessage } from '../../utils/mediaReadiness';

// Sunucu activeConnections girdisi kaybolmuşsa oturumu ID ile kapatabilsin diye sessionId taşınır.
function endCallPayload(state, reason) {
    const sessionId = state.activeCall?.sessionId || state.sessionId || null;
    const payload = {};
    if (sessionId) payload.sessionId = sessionId;
    if (reason) payload.reason = reason;
    return payload;
}

const createCallSlice = (set, get) => ({
    // Çağrı durumu
    activeCall: null,
    callStatus: null, // 'waiting', 'connecting', 'connected', 'ended'
    sessionId: null,
    callEndEmitted: false, // FE-143: endCall/cleanEndCall aynı görüşmede iki kez 'end-call' emit etmesin

    // Çağrı durumunu değiştirme
    setCallStatus: (status) => {
        set({ callStatus: status });
    },

    // Session ID'yi ayarlama
    setSessionId: (sessionId) => {
        set({ sessionId });
    },

    // Bağlantı isteği gönderme (temsilcilerin müşterilere)
    requestConnection: (customerId) => {
        const { socket, userType } = get();

        if (!socket || !socket.connected) {
            return;
        }

        if (userType !== 'agent') {
            return;
        }


        socket.emit('request-connection', {
            agentId: socket.id,
            customerId
        });

        set({
            activeCall: { customerId },
            callStatus: 'waiting'
        });

    },

    // Bağlantı isteğini reddetme
    rejectConnection: () => {
        const { socket, activeCall } = get();

        if (!socket || !activeCall) {
            return;
        }


        // Eğer sunucuda bir 'reject-connection' olayı varsa, bunu gönder
        // socket.emit('reject-connection', { agentId: activeCall.agentId });

        // Yerel durumu temizle
        set({
            activeCall: null,
            callStatus: null
        });

        toast('Bağlantı isteği reddedildi');
    },

    // Görüşmeyi sonlandırma - DÜZELTİLDİ ✅
    endCall: () => {
        const { socket, peerConnection, localStream, remoteStream, activeCall, callEndEmitted } = get();


        // Session ID'yi güvenli şekilde al
        const currentSessionId = activeCall?.sessionId || get().sessionId;

        // FE-143: aynı görüşmede endCall/cleanEndCall'dan yalnızca ilki 'end-call' emit etsin
        if (socket && !callEndEmitted) {
            socket.emit('end-call', endCallPayload(get()));
            set({ callEndEmitted: true });
        }

        // WebRTC kaynakları temizle
        if (peerConnection) {
            peerConnection.close();
        }

        // Medya akışlarını kapat
        if (localStream) {
            localStream.getTracks().forEach(track => track.stop());
        }

        if (remoteStream) {
            remoteStream.getTracks().forEach(track => track.stop());
        }

        // State'i güncelle - sessionId'yi koru
        const newState = {
            activeCall: currentSessionId ? {
                sessionId: currentSessionId,
                customerId: activeCall?.customerId,
                customerSocketId: activeCall?.customerSocketId,
                customerName: activeCall?.customerName,
                idNumber: activeCall?.idNumber,
                issue: activeCall?.issue
            } : null,
            callStatus: 'completed',
            sessionId: currentSessionId, // sessionId'yi store'da da tut
            peerConnection: null,
            localStream: null,
            remoteStream: null,
            // Recording state'lerini temizle
            recordedVideo: null,
            localRecordedVideo: null,
            isRecording: false,
            isLocalRecording: false
        };


        // LocalStorage'ı temizle ve yeni sessionId'yi kaydet
        if (currentSessionId) {
            // Eski sessionId'leri temizle
            localStorage.removeItem('lastCallSessionId');
            localStorage.removeItem('currentCallSessionId');

            // Yeni sessionId'yi kaydet
            localStorage.setItem('currentCallSessionId', currentSessionId);
        }

        set(newState);

        // ✅ Gelişmiş görüşme sonrası müşteri listesi yenileme
        setTimeout(() => {
            get().refreshAfterCall();
        }, 1000);
    },

    // Oturum açıldıktan sonra temsilcinin kamera/mikrofonu açılamadı ya da görüşmede kayboldu.
    // Sunucu oturumu cancelled/agent_media_failed kapatır, müşteriyi kuyruğun başına alır; temsilci
    // 'technical_issue' olur. Görüşme sonucu formu ve anket açılmaz.
    abortCallForAgentMediaFailure: (errorCode) => {
        const state = get();
        const { socket, peerConnection, localStream, remoteStream, callEndEmitted } = state;
        if (!state.activeCall && !peerConnection) return;

        const agentId = state.user?.id;
        // Görüşme zaten normal biçimde bitmişse (karşı taraf kapattı, sonuç formu açık) onu silme:
        // yalnız cihaz durumunu kaydet ve temsilciyi technical_issue'ya al.
        if (state.callStatus === 'completed' || callEndEmitted) {
            if (localStream) localStream.getTracks().forEach(track => track.stop());
            set({ localStream: null });
            get().setMediaReadiness(false, errorCode);
            if (agentId) get().setTechnicalIssue(agentId, 'Kamera/mikrofon hazır değil', { force: true });
            return;
        }

        if (socket) {
            socket.emit('end-call', endCallPayload(state, 'agent_media_failed'));
        }

        if (peerConnection) {
            if (peerConnection._iceTimerRef?.timer) {
                clearTimeout(peerConnection._iceTimerRef.timer);
                peerConnection._iceTimerRef.timer = null;
            }
            try { peerConnection.close(); } catch (_) { }
        }
        if (localStream) localStream.getTracks().forEach(track => track.stop());
        if (remoteStream) remoteStream.getTracks().forEach(track => track.stop());

        set({
            activeCall: null,
            callStatus: null,
            sessionId: null,
            callEndEmitted: true,
            peerConnection: null,
            localStream: null,
            remoteStream: null
        });

        get().setMediaReadiness(false, errorCode);
        toast.error(`Görüşme başlatılamadı: ${mediaErrorMessage(errorCode)} Müşteri sıraya geri alındı.`);

        if (agentId) get().setTechnicalIssue(agentId, 'Kamera/mikrofon hazır değil', { force: true });
    },

    // Görüşmeyi tamamen sonlandırma (temsilci listesine dönüş için)
    cleanEndCall: () => {
        const { socket, peerConnection, localStream, remoteStream, callEndEmitted } = get();

        // FE-143: aynı görüşmede endCall/cleanEndCall'dan yalnızca ilki 'end-call' emit etsin
        if (socket && !callEndEmitted) {
            socket.emit('end-call', endCallPayload(get()));
            set({ callEndEmitted: true });
        }

        // WebRTC kaynakları temizle
        if (peerConnection) {
            peerConnection.close();
        }

        // Medya akışlarını kapat
        if (localStream) {
            localStream.getTracks().forEach(track => track.stop());
        }

        if (remoteStream) {
            remoteStream.getTracks().forEach(track => track.stop());
        }

        // LocalStorage'ı temizle
        localStorage.removeItem('lastCallSessionId');
        localStorage.removeItem('currentCallSessionId');

        // ✅ Tüm state'leri temizle
        set({
            activeCall: null,
            callStatus: 'ended', // Müşteri listesine dönüş için
            sessionId: null,
            peerConnection: null,
            localStream: null,
            remoteStream: null,
            // Recording state'lerini temizle
            recordedVideo: null,
            localRecordedVideo: null,
            isRecording: false,
            isLocalRecording: false
        });

        // ✅ Gelişmiş görüşme sonrası müşteri listesi yenileme
        setTimeout(() => {
            get().refreshAfterCall();
        }, 500); // 500ms bekle ve listeyi yenile

    },

});

export default createCallSlice; 
